import isToday from 'date-fns/is_today';
import isTomorrow from 'date-fns/is_tomorrow';
import {
    MAX_SINGLE_DIGIT,
    DATE_TIME_PADDING,
    MONTH_INDEX_CORRECTION,
    TODAY_I18N_KEY,
    TOMORROW_I18N_KEY,
    dateFormats,
    DATE_TIME_FORMAT_DEFAULT,
} from './constants';
import i18n from './../utils/i18n';
import { getCountryIso } from './user';

/**
 * Cdd a zero befor info if lower than 10
 * @param {Integer} timeInfo get day, hour, minute digits.
 * @return {String} return info formatted
 */
export const setZeroMaskTimeInfo = (timeInfo) => {
    return timeInfo > MAX_SINGLE_DIGIT ? timeInfo : `${DATE_TIME_PADDING}${timeInfo}`;
};

/**
 * Returns a cross platform datetime for Web Browser, Android and iOS.
 * @param {String} dateTimeString Date and time in ISO 8601 fromat in UTC. Format: yyyy-mm-ddThh:mm:ss+00:00
 * @return {Date} returns a Date Object.
 */
export const getCrossPlatformDateTime = (dateTimeString) => {
    return new Date(dateTimeString);
};

/**
 * Returns a formatted hour and minute
 * @param {String|Date} dateTime Date and time. Format: yyyy-mm-dd hh:mm:ss
 * @returns {String} EX: 09:10
 *
 * @deprecated Use <Time> component instead
 */
export const getTimeLabel = (dateTime) => {
    let time;
    if (typeof dateTime === 'string') {
        time = getCrossPlatformDateTime(dateTime);
    } else {
        time = dateTime;
    }

    let hour = time.getHours();
    hour = setZeroMaskTimeInfo(hour);
    let minute = time.getMinutes();
    minute = setZeroMaskTimeInfo(minute);

    return `${hour}:${minute}`;
};

/**
 * Returns a formatted date label: Today, Tomorrow or getLocalizedDateFormat
 * @param {String} dateTime Date and time. Format: yyyy-mm-dd hh:mm:ss
 * @returns {String} EX: "Today", "Tomorrow" or getLocalizedDateFormat, based on default format
 */
export const getTodayTomorrowDateLabel = (dateTime) => {
    let time;
    if (typeof dateTime === 'string') {
        time = getCrossPlatformDateTime(dateTime);
    } else {
        time = dateTime;
    }

    if (isToday(time)) {
        return i18n.t(TODAY_I18N_KEY);
    }

    if (isTomorrow(time)) {
        return i18n.t(TOMORROW_I18N_KEY);
    }

    const dateFormat = getLocalizedDateFormat();

    return { dateTime: time.toString(), format: dateFormat };
};

/**
 * Returns a formatted date string
 * @param {Date} dateTimeObject Date object.
 * @returns {String} Format: yyyy-mm-dd. Ex: 2019-01-14
 */
export const convertDateToString = (dateTimeObject) => {
    let dateString = `${setZeroMaskTimeInfo(dateTimeObject.getFullYear())}-`;
    dateString += `${setZeroMaskTimeInfo(dateTimeObject.getMonth()+MONTH_INDEX_CORRECTION)}-`;
    dateString += `${setZeroMaskTimeInfo(dateTimeObject.getDate())}`;

    return dateString;
};

/**
 * Returns a formatted time string
 * @param {Date} dateTimeObject Date object.
 * @returns {String} Format: hh:mm:ss. Ex: 18:32:52
 */
export const convertTimeToString = (dateTimeObject) => {

    let timeString = `${setZeroMaskTimeInfo(dateTimeObject.getHours())}:`;
    timeString += `${setZeroMaskTimeInfo(dateTimeObject.getMinutes())}:`;
    timeString += `${setZeroMaskTimeInfo(dateTimeObject.getSeconds())}`;

    return timeString;
};

/**
 * Returns a formatted date and time string
 * @param {Date} dateTimeObject Date object.
 * @returns {String} Format: yyyy-mm-dd hh:mm:ss. Ex: 2019-01-14 18:32:52
 */
export const convertDateTimeToString = (dateTimeObject) => {

    return `${convertDateToString(dateTimeObject)} ${convertTimeToString(dateTimeObject)}`;
};


/**
 * Returns the correct format based on the format for that language
 * @param {String} dateFormat Format example: 'dddd, D MMMM' - see https://unicode.org/reports/tr35/tr35-dates.html
 * Note - date-fns is *supposed* to use Unicode tokens, but it is not consistent, e.g. "EEEE" vs. "dddd"
 * @returns {String} Format, default DATE_TIME_FORMAT_DEFAULT
 */
export const getLocalizedDateFormat = (dateFormat) => {
    let countryIso = getCountryIso();
    const formatDate = dateFormats[countryIso];

    if(typeof formatDate === 'undefined') {
        countryIso = 'default';
    }

    const localizedDate = dateFormats[countryIso] && dateFormats[countryIso][dateFormat];

    if (typeof localizedDate !== 'undefined') {
        return localizedDate;
    }

    return dateFormats[countryIso][DATE_TIME_FORMAT_DEFAULT];
};

/**
 * Generate an ATOM (ISO8601) formatted from a Date object (e.g. '2022-08-30T09:07:01+00:00)
 * Note that Date.prototype.toISOString() can return a 24 or 27 length string (according to MDN) so we use
 * custom formatting
 * Note that this function assumes a local time (if passed) and returns a UTC datetime
 * @param {String} dateTime the date/time to be represented, defaults to now
 * @returns {String|Boolean} the formatted dateTime or false if Date object is invalid
 */
export const getATOMDateTimeFormat = (dateTime = null) => {
    let d; // the Date object
    if (dateTime === null) {
        d = new Date();
    } else {
        d = new Date(dateTime);
        if (!(d instanceof Date) || isNaN(d)) {

            return false;
        }
    }

    // helper
    const pad = (num) => num.toString().padStart(2, 0);

    const dateString = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
    const timeString = `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
    const tzIdentifier = '+00:00';

    return `${dateString}T${timeString}${tzIdentifier}`;
};
